/**
 * Notification schemas
 *
 * Validation schemas for Notifications feature.
 */

import { z } from 'zod';

// ═══════════════════════════════════════════════════════════════════════
// NOTIFICATION TYPE SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Notification type enum
 */
export const notificationTypeSchema = z.enum([
  // System notifications
  'system_announcement',
  'system_maintenance',
  'system_update',
  // Journey notifications
  'journey_milestone',
  'journey_reminder',
  // Hero notifications
  'checkup_reminder',
  'checkup_results',
  'teleportal_task_due',
  'teleportal_task_completed',
  'teleportal_document_verified',
  'qultureup_new_mission',
  'qultureup_mission_completed',
  'care_checkpoint_due',
  'care_support_response',
  'passport_stamp_earned',
  'passport_badge_earned',
  'passport_level_up',
  // Career notifications
  'career_opportunity_match',
  'career_application_update',
  // Billing notifications
  'billing_payment_due',
  'billing_payment_success',
  'billing_payment_failed',
  'billing_subscription_expiring',
  // Social notifications
  'family_invitation',
  'family_member_joined',
  // CoPilot notifications
  'copilot_suggestion',
]);

/**
 * Notification priority enum
 */
export const notificationPrioritySchema = z.enum(['low', 'normal', 'high', 'urgent']);

/**
 * Notification channel enum
 */
export const notificationChannelSchema = z.enum(['in_app', 'email', 'push', 'sms']);

// ═══════════════════════════════════════════════════════════════════════
// NOTIFICATION LISTING SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * List notifications schema
 */
export const listNotificationsSchema = z.object({
  types: z.array(notificationTypeSchema).optional(),
  unreadOnly: z.boolean().default(false),
  priority: notificationPrioritySchema.optional(),
  fromDate: z.coerce.date().optional(),
  toDate: z.coerce.date().optional(),
  page: z.number().int().min(1).default(1),
  limit: z.number().int().min(1).max(100).default(20),
});

/**
 * Get unread count schema
 */
export const getUnreadCountSchema = z.object({
  types: z.array(notificationTypeSchema).optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// NOTIFICATION ACTION SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Mark as read schema
 */
export const markAsReadSchema = z.object({
  notificationId: z.uuid(),
});

/**
 * Mark all as read schema
 */
export const markAllAsReadSchema = z.object({
  types: z.array(notificationTypeSchema).optional(),
  beforeDate: z.coerce.date().optional(),
});

/**
 * Delete notification schema
 */
export const deleteNotificationSchema = z.object({
  notificationId: z.uuid(),
});

/**
 * Delete all notifications schema
 */
export const deleteAllNotificationsSchema = z.object({
  readOnly: z.boolean().default(true), // Only delete read notifications by default
  beforeDate: z.coerce.date().optional(),
});

// ═══════════════════════════════════════════════════════════════════════
// NOTIFICATION SETTINGS SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Notification setting schema
 */
export const notificationSettingSchema = z.object({
  type: notificationTypeSchema,
  enabled: z.boolean(),
  channels: z.array(notificationChannelSchema).min(1).optional(),
});

/**
 * Update notification settings schema
 */
export const updateNotificationSettingsSchema = z.object({
  settings: z.array(notificationSettingSchema),
});

/**
 * Channel preferences schema
 */
export const channelPreferencesSchema = z.object({
  email: z.object({
    enabled: z.boolean(),
    digestFrequency: z.enum(['immediate', 'daily', 'weekly']).default('immediate'),
  }),
  push: z.object({
    enabled: z.boolean(),
    quietHoursStart: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional(), // HH:MM format
    quietHoursEnd: z
      .string()
      .regex(/^\d{2}:\d{2}$/)
      .optional(),
  }),
  sms: z.object({
    enabled: z.boolean(),
    urgentOnly: z.boolean().default(true),
  }),
});

/**
 * Update channel preferences schema
 */
export const updateChannelPreferencesSchema = z.object({
  preferences: channelPreferencesSchema,
});

// ═══════════════════════════════════════════════════════════════════════
// PUSH NOTIFICATION SCHEMAS
// ═══════════════════════════════════════════════════════════════════════

/**
 * Register push token schema
 */
export const registerPushTokenSchema = z.object({
  token: z.string().min(1),
  platform: z.enum(['ios', 'android', 'web']),
  deviceId: z.string().optional(),
  deviceName: z.string().max(100).optional(),
});

/**
 * Unregister push token schema
 */
export const unregisterPushTokenSchema = z.object({
  token: z.string().min(1),
});

// ═══════════════════════════════════════════════════════════════════════
// TYPE EXPORTS
// ═══════════════════════════════════════════════════════════════════════

export type NotificationType = z.infer<typeof notificationTypeSchema>;
export type NotificationPriority = z.infer<typeof notificationPrioritySchema>;
export type NotificationChannel = z.infer<typeof notificationChannelSchema>;
export type ListNotificationsInput = z.infer<typeof listNotificationsSchema>;
export type GetUnreadCountInput = z.infer<typeof getUnreadCountSchema>;
export type MarkAsReadInput = z.infer<typeof markAsReadSchema>;
export type MarkAllAsReadInput = z.infer<typeof markAllAsReadSchema>;
export type DeleteNotificationInput = z.infer<typeof deleteNotificationSchema>;
export type DeleteAllNotificationsInput = z.infer<typeof deleteAllNotificationsSchema>;
export type NotificationSetting = z.infer<typeof notificationSettingSchema>;
export type UpdateNotificationSettingsInput = z.infer<typeof updateNotificationSettingsSchema>;
export type ChannelPreferences = z.infer<typeof channelPreferencesSchema>;
export type UpdateChannelPreferencesInput = z.infer<typeof updateChannelPreferencesSchema>;
export type RegisterPushTokenInput = z.infer<typeof registerPushTokenSchema>;
export type UnregisterPushTokenInput = z.infer<typeof unregisterPushTokenSchema>;
